// src/components/artwork/tabs/ArtworkProvenanceTab.jsx
import React, { useMemo, useState } from "react";
import ProvenanceModal from "../modals/ProvenanceModal";
import { useTranslation } from "react-i18next";

const normalizeProvenance = (raw) => {
  if (!raw) return [];

  let value = raw;

  if (typeof raw === "string") {
    try {
      value = JSON.parse(raw);
    } catch {
      value = raw;
    }
  }

  if (Array.isArray(value)) return value;

  if (typeof value === "object") {
    return [
      {
        id: Date.now(),
        date: value.date ?? null,
        type: value.type ?? "",
        description: value.description ?? "",
        location: value.location ?? "",
      },
    ];
  }

  return [
    {
      id: Date.now(),
      date: null,
      type: "",
      description: String(value),
      location: "",
    },
  ];
};

const TYPE_KEYS = ["direct_artiste", "echange_production", "galerie", "vente_privee", "encheres", "succession", "don", "collection_privee", "autre"];

const ArtworkProvenanceTab = ({
  formData,
  artwork,
  setFormData,
  readOnly = false,
  canEdit = null,
}) => {
  const { t, i18n } = useTranslation();
  const isEditable = canEdit === true || readOnly !== true;
  const [isModalOpen, setIsModalOpen] = useState(false);
  // null = ajout, objet = édition
  const [editingItem, setEditingItem] = useState(null);

  const rawProvenanceSource =
    formData?.provenance ?? artwork?.provenance ?? null;

  const provenance = useMemo(
    () => normalizeProvenance(rawProvenanceSource),
    [rawProvenanceSource]
  );

  const updateProvenance = (next) => {
    if (!isEditable) return;
    if (typeof setFormData !== "function") return;
    setFormData((prev) => ({
      ...(prev || {}),
      provenance: next,
    }));
  };

  const handleAddProvenance = (newItem) => {
    if (!isEditable) return;
    const item = {
      id: Date.now(),
      type: newItem.type || "",
      date: newItem.date || null,
      description: newItem.description || "",
      location: newItem.location || "",
    };
    updateProvenance([...(provenance || []), item]);
  };

  const handleUpdateProvenance = (updatedItem) => {
    if (!isEditable) return;
    updateProvenance(
      provenance.map((p) =>
        p.id === updatedItem.id ? { ...p, ...updatedItem } : p
      )
    );
  };

  const handleRemoveProvenance = (id) => {
    if (!isEditable) return;
    updateProvenance(provenance.filter((p) => p.id !== id));
  };

  const openAdd = () => {
    if (!isEditable) return;
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const openEdit = (item) => {
    if (!isEditable) return;
    setEditingItem(item);
    setIsModalOpen(true);
  };

  return (
    <div className="space-y-4 tab-content" style={{ minHeight: "400px" }}>
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-serif font-semibold">{t("artworkProvenance.title")}</h2>

        {isEditable && (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); openAdd(); }}
            className="relative z-10 inline-flex items-center rounded-full border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-4 h-4 mr-1"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 4.5v15m7.5-7.5h-15"
              />
            </svg>
            {t("artworkProvenance.add")}
          </button>
        )}
      </div>

      {provenance.length === 0 ? (
        <p className="text-gray-500 text-sm italic">
          {t("artworkProvenance.empty")}
        </p>
      ) : (
        <div className="space-y-3">
          {provenance.map((item) => (
            <div
              key={item.id}
              className="flex justify-between gap-4 rounded-md border border-gray-200 bg-white px-4 py-3"
            >
              <div className="flex-1 min-w-0">
                {/* Badge type */}
                {item.type && TYPE_KEYS.includes(item.type) && (
                  <span className="inline-block text-xs font-medium uppercase tracking-wider bg-gray-100 text-gray-600 px-2 py-0.5 rounded mb-1">
                    {t(`artworkProvenance.types.${item.type}`)}
                  </span>
                )}

                <div className="flex items-center gap-2 flex-wrap">
                  {item.date && (
                    <span className="text-xs text-gray-500">
                      {new Date(item.date).toLocaleDateString(i18n.language)}
                    </span>
                  )}
                  <span className="text-sm font-medium">
                    {item.description}
                  </span>
                </div>

                {item.location && (
                  <p className="text-xs text-gray-500 mt-0.5">{item.location}</p>
                )}
              </div>

              {isEditable && (
                <div className="flex items-center gap-2 shrink-0">
                  {/* Éditer */}
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); openEdit(item); }}
                    className="text-gray-400 hover:text-gray-700 transition-colors"
                    title={t("artworkProvenance.edit")}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      strokeWidth="1.5"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125"
                      />
                    </svg>
                  </button>

                  {/* Supprimer */}
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleRemoveProvenance(item.id); }}
                    className="text-red-400 hover:text-red-600 transition-colors"
                    title={t("artworkProvenance.delete")}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      strokeWidth="1.5"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {isModalOpen && isEditable && (
        <ProvenanceModal
          onClose={() => {
            setIsModalOpen(false);
            setEditingItem(null);
          }}
          onAddProvenance={handleAddProvenance}
          onUpdateProvenance={handleUpdateProvenance}
          initialData={editingItem}
        />
      )}
    </div>
  );
};

export default ArtworkProvenanceTab;